import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SecurityContext,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { EditorComponent } from '@tinymce/tinymce-angular';

import { UpdatePropRequest } from '@blaze-runtime/engine-domain';
import { markEventAsHandled } from '@blaze-runtime/engine-util';
import {
  DEFAULT_ELEMENT_PADDING,
  type ElementFormState,
  makeElementAlignmentGridStyles,
  makeElementAlignmentStyles,
  makeFontStyles,
  makeFontStyleStyles,
  toCssString,
} from '@blaze-shared/elements-domain';
import { getBackground } from '@blaze-shared/elements-domain';
import { getDisplayStringFromLinkValue, isDatasetLinkValue } from '@blaze-shared/schema-domain';
import { isNullish, setCssPropertyValue } from '@blaze-shared/shared-util';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { initializeTinyMceConfig } from '../../utils/elements/tiny-mce.utils';
import { CanvasElementBaseComponent } from '../__base/base.component';

@Component({
  selector: 'be-text-area',
  templateUrl: './text-area.component.html',
  styleUrls: ['./text-area.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TextAreaComponent
  extends CanvasElementBaseComponent<'textArea'>
  implements OnInit, OnChanges, OnDestroy, CanvasElementComponent<'textArea'>
{
  private readonly elementRef = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);

  editor = viewChild(EditorComponent);

  @Input() value!: string;
  @Input() formState?: ElementFormState;

  @Output() updateProp = new EventEmitter<UpdatePropRequest>();

  private areaClickEvent?: MouseEvent;
  private editorValue?: string;

  canBeLiveEdited = false;
  isLiveEdit = signal(false);
  isEditorLoaded = signal(false);

  readonly elementKeyRoot = 'b-ta';
  elementKey!: string;
  editorKey!: string;
  wrapperSectionKey!: string;

  containerStyles!: Record<string, string | undefined>;
  contentWrapperStyles!: Record<string, string | undefined>;
  textStyles!: Record<string, string | undefined>;
  editorPadding?: string;
  editorAlign?: string;
  editorConfig: EditorComponent['init'];

  @HostBinding('class.is-viewing') get isViewing(): boolean {
    return !this.isLiveEdit();
  }

  get hasValidationError(): boolean | undefined {
    return this.formState?.errors?.required || !!this.formState?.errors?.invalid;
  }

  constructor() {
    super();
    this.editorConfig = initializeTinyMceConfig({
      initHandler: () => {
        this.isEditorLoaded.set(true);
        if (this.areaClickEvent) {
          setTimeout(() => {
            this.onAreaClicked(this.areaClickEvent as MouseEvent);
            this.areaClickEvent = undefined;
          });
        }
      },
      inputHandler: (content: string) => {
        this.updateOutput(content);
      },
      followCursor: true,
      isEditing: this.isLiveEdit,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    let valueChanged = false;
    if (changes['props']) {
      if (!this.elementKey) {
        this.elementKey = `${this.elementKeyRoot}-${this.elementId}`;
        this.editorKey = `editor-${this.elementId}`;
        this.wrapperSectionKey = `section-${this.elementId}`;
        if (this.editorConfig) {
          this.editorConfig.fixed_toolbar_container_target = this.elementRef.nativeElement;
        }
      }

      const { input } = this.props?.content || {};

      if (!this.bindingResults?.['value']) {
        this.value = this.getSafeValue(input?.value);
        valueChanged = true;
      }

      this.updateStyles();
    }

    if (changes['value'] && isDatasetLinkValue(this.value)) {
      this.value = getDisplayStringFromLinkValue(this.value.display);
      valueChanged = true;
    }

    if (isNullish(this.value)) {
      this.value = '';
    }

    this.canBeLiveEdited = !!(this.isInEditor && !this.bindingResults?.['value']);
    const editor = this.editor()?.editor;
    if (valueChanged && editor && !editor.hasFocus() && this.value !== this.editorValue) {
      this.editorValue = this.value;
      editor.setContent(this.value);
    }
  }

  ngOnInit(): void {
    this.stylesCleanup.register([this.elementKey, `${this.elementKey}-font`]);
  }

  ngOnDestroy(): void {
    this.stylesCleanup.dispose([this.elementKey, `${this.elementKey}-font`]);
  }

  onEditorMousedown(event: Event): void {
    markEventAsHandled(event);
  }

  onValueChanged(e: any): void {
    this.updateOutput(e.event.level.content);
  }

  onAreaClicked(event: MouseEvent): void | boolean {
    if (!this.canBeLiveEdited) {
      return;
    }
    const editor = this.editor()?.editor;

    if (!editor) {
      this.areaClickEvent = event;
      this.isLiveEdit.set(true);
      return;
    }
    if (!this.isLiveEdit()) {
      this.isLiveEdit.set(true);
      return;
    }

    const editorBody = editor.bodyElement;
    if (!editorBody) {
      return;
    }

    const yDiff = editorBody.offsetTop - event.offsetY;
    if (yDiff > 0) {
      // clicked above
      if (yDiff < 32 && event.offsetX > editorBody.offsetLeft + editorBody.offsetWidth / 2) {
        // but in the right half and close enough to the editor
        this.makeEditorSelection(true);
      } else {
        this.makeEditorSelection();
      }
    } else if (event.offsetY > editorBody.offsetTop + editorBody.offsetHeight) {
      // clicked below
      this.makeEditorSelection(true);
    }
  }

  toggleLiveEdit(state: boolean): void {
    if (this.isLiveEdit() === state) {
      return;
    }
    this.isLiveEdit.set(state);
  }

  private updateOutput(payload: string): void {
    this.updateProp.emit({ path: 'content.input.value', payload });
  }

  private updateStyles(): void {
    const { content, style } = this.props ?? {};
    const { font, container } = content || {};
    const { background, general } = style || {};

    const containerStyles: Record<string, string | undefined> = {};
    const contentWrapperStyles = {
      ...makeElementAlignmentGridStyles(container?.offset.align),
    };

    const boldWeight = this.fontsService.getFontWeight(font?.fontFamily, '500', '700');
    setCssPropertyValue('--text-area-bold', boldWeight);

    const fontStyles = {
      ...makeFontStyles(font),
      ...makeFontStyleStyles(font?.style, 'var(--text-area-bold)'),
      color: general?.textColor.color1,
      ['font-weight']: this.fontsService.getFontWeight(font?.fontFamily, font?.fontWeight),
      ['letter-spacing']: font?.letterSpacing,
    };

    if (background) {
      containerStyles['background'] = getBackground(background.fill);
      containerStyles['border'] =
        background.outline &&
        background.outlineWidth &&
        background?.outline.selectorType !== 'none' &&
        background?.outlineWidth !== '0px'
          ? `${background.outlineWidth} solid ${background.outline.color1}`
          : undefined;
      containerStyles['borderRadius'] = background.radius !== '0px' ? background.radius : undefined;
    }

    this.editorPadding = container?.offset?.padding ?? DEFAULT_ELEMENT_PADDING;
    if (container?.offset?.align) {
      const align = makeElementAlignmentStyles(container?.offset?.align);
      this.editorAlign = align?.alignItems;
    }
    if (container) {
      containerStyles['padding'] = container.offset?.padding ?? DEFAULT_ELEMENT_PADDING;
      containerStyles['overflow'] = container.overflow;
    }

    this.textStyles = fontStyles;
    this.containerStyles = containerStyles;
    this.contentWrapperStyles = contentWrapperStyles;

    this.stylesCleanup.update(
      `${this.elementKey}`,
      `
    .${this.elementKey} a, #${this.editorKey}.mce-content-body a { color: ${general?.linkColor.color1} }
    #${this.editorKey}.mce-content-body { ${toCssString(this.textStyles)};
    `,
    );

    this.stylesCleanup.update(
      `${this.elementKey}-font`,
      `.${this.elementKey}-font { --text-area-bold: ${boldWeight}; }`,
    );
  }

  private makeEditorSelection(goToEnd = false): void {
    const editor = this.editor()?.editor;
    if (!editor) {
      return;
    }

    const editorNodes = editor.dom.getRoot().childNodes;
    const node = editorNodes[goToEnd ? editorNodes.length - 1 : 0];

    editor.selection.select(node);
    editor.selection.collapse(!goToEnd);
  }

  private getSafeValue(value: string | undefined): string {
    if (!value) {
      return '';
    }
    /** replace new lines with p-tags.
     * we also used to remove `<br data-mce-bogus="1">`, but when it started
     * to cause layout issues when switching between text-editor/preview (BL-9290) */
    if (!value.includes('\n')) {
      return value;
    }
    const raw = value
      .replaceAll('\n\n', '\n')
      .split('\n')
      .map((l) => `<p>${l}</p>`)
      .join('<br>');
    const safe = this.sanitizer.sanitize(SecurityContext.HTML, raw) ?? '';
    return safe;
  }
}
